import { Injectable } from '@angular/core';
import { Market, MarketAccount, PaginatedMarketDTO, TaskType, MarketTeamUserDetailsByType, MarketTeamUserDetailsOutput, MarketsInput, StateAbbreviation } from '../../api/models/market-model';
import { of } from 'rxjs/observable/of';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { Address } from '../models/address';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { environment } from '../../environments/environment';
import { PaginatedRequest } from '../models/paginated-request';
import { Account } from '../models/account';
import { BillTo } from '../models/bill-to';

@Injectable()
export class MarketService {

  baseUrl = environment.marketServiceBaseUrl;

  constructor(private http: HttpClient) {
  }

  saveMarket(market: Market) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    if (market.id) {
      const url = `${this.baseUrl}markets/${market.id}`;
      return this.http.put<Market>(url, market, { headers });
    } else {
      const url = `${this.baseUrl}markets`;
      return this.http.post<Market>(url, market, { headers });
    }
  }

  deleteMarket(marketId: string) {
    const url = `${this.baseUrl}markets/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.delete<void>(url, { headers });
  }

  getMarkets(orgId: string) {
    const url = `${this.baseUrl}markets/organization/${orgId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Market[]>(url, { headers })
      .catch(error => {
        if (error.status === 404) {
          return of([]);
        } else {
          throw error;
        }
      });
  }

  getMarketsByOrgId(orgId: string){
    const url = `${this.baseUrl}markets/byOrgId`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams()
    .set('orgId', orgId)
    .set('page', '0')
    .set('size', '2000');
    return this.http.get<PaginatedMarketDTO>(url, { params, headers })
      .map(response => response.records);
  }

  getMarketsList(paginatedRequest: PaginatedRequest, marketsInput?: MarketsInput) {
    const url = `${this.baseUrl}markets`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      let params = new HttpParams()
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize)
      .set('sort', paginatedRequest.sorting);
      if (marketsInput) {
        params = params.set('marketsInput', JSON.stringify(marketsInput));
      }
    return this.http.get<PaginatedMarketDTO>(url, { params, headers });
  }

  getMarketsListMinimalInfo(paginatedRequest: PaginatedRequest, marketsInput?: MarketsInput) {
    const url = `${this.baseUrl}markets/minimalInfo`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      let params = new HttpParams()
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize)
      .set('sort', paginatedRequest.sorting);
    return this.http.post<PaginatedMarketDTO>(url, marketsInput, { params, headers });
  }


  getMarketById(marketId: string) {
    const url = `${this.baseUrl}markets/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Market>(url, { headers });
  }

  getMarketsByIds(marketIds: string[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      const url = `${this.baseUrl}markets/list`;
      return this.http.post<Market[]>(url, marketIds, { headers });

  }

  saveAddress(marketId: string, address: Address) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    if (address.id) {
      const url = `${this.baseUrl}markets/${marketId}/addresses/${address.id}`;
      return this.http.put<Address>(url, address, { headers });
    } else {
      const url = `${this.baseUrl}markets/${marketId}/addresses`;
      return this.http.post<Address>(url, address, { headers });
    }
  }

  saveAddresses(marketId: string, addresses: Address[]) {
    return forkJoin(addresses.map(a => this.saveAddress(marketId, a)));
  }

  getAddresses(marketId: string) {
    const url = `${this.baseUrl}markets/${marketId}/addresses`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Address[]>(url, { headers });
  }

  getMarket(marketId: string) {
    const url = `${this.baseUrl}markets/${marketId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Market>(url, { headers });
  }

  getCheckoutAddresses(marketId: string) {
    const url = `${this.baseUrl}markets/${marketId}/checkoutAddresses`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Address[]>(url, { headers });
  }

  getAddress(marketId: string, addressId: string) {
    const url = `${this.baseUrl}markets/${marketId}/addresses/${addressId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Address>(url, { headers });
  }

  deleteAddress(marketId: string, addressId: string) {
    const url = `${this.baseUrl}markets/${marketId}/addresses/${addressId}`;
    return this.http.delete<void>(url);
  }

  deleteAddresses(marketId: string, addressIds: string[]) {
    return forkJoin(addressIds.map(a => this.deleteAddress(marketId, a)));
  }

  findAddressesByText(marketId: string, text: string) {
    const url = `${this.baseUrl}markets/${marketId}/addresses/search`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('text', text);
    return this.http.get<Address[]>(url, { params, headers });
  }

  saveAddressList(marketId: string, addressList: Address[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      const url = `${this.baseUrl}markets/${marketId}/addresses/updateListOfAddress/`;
      return this.http.put<string>(url, addressList, { headers, responseType: 'text' as 'json'  });

  }

  getMarketTasks() {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}markets/taskType`;
    return this.http.get<TaskType[]>(url, { headers });
  }

  getMarketPermissionDetailsByType(requestVariable: MarketTeamUserDetailsByType) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      const url = `${this.baseUrl}markets/teamOrUser/byType`;
      return this.http.post<MarketTeamUserDetailsOutput>(url, requestVariable, {headers});
  }

  getAccount(accountNumber: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
      const url = `${this.baseUrl}accounts/${accountNumber}`;
      return this.http.get<Account>(url, {headers});
  }

  getBillingAddresses(marketId: string) {
    const url = `${this.baseUrl}markets/${marketId}/bill-to-address`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<BillTo>(url, { headers });
  }

  getMarketsByMarketType(marketType: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}markets/types/${marketType}`;
    return this.http.get<Market[]>(url, { headers });
  }

  getStateAbbreviations() {
    const url = `${this.baseUrl}markets/addresses/state/abbreviations`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<StateAbbreviation[]>(url, { headers });
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/api/services/market.service.ts